import { InfiniteOptions } from '@cfra-nextgen-frontend/shared/src/components/InfiniteScroll/InfiniteOptions';
import type { SendSingleInfiniteRequest } from '@cfra-nextgen-frontend/shared/src/components/Screener/api/screener';
import type { Hit } from '@cfra-nextgen-frontend/shared/src/components/TypeSearch/types/opensearch';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { debounce } from 'lodash';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
    type AccessibilitySecurity,
    type AccessibilitySearchResponse,
    SECURITY_SUGGESTER_PATH,
    SECURITY_SUGGESTER_SIZE,
    getSecuritySuggesterInfiniteRequestParamsConfig,
} from '../api/security';
import {
    CHART_DATA_PATH,
    CHART_TYPE_DISPLAY_NAMES,
    type ChartTypeCode,
    type ChartTypeOption,
    getChartTypesForSecurity,
    resolveS3SecurityType,
    resolveSecurityId,
} from '../api/chartConfig';
import { sendSingleRequest } from '../../../utils/api';
import { ApiNames, RequestTypes } from '@cfra-nextgen-frontend/shared/src/utils/enums';
import {
    chartTypeSectionSx,
    dropdownContainerSx,
    emptyStateSx,
    loadChartButtonSx,
    mainContentPanelSx,
    mainContentSx,
    pageContainerSx,
    SCROLL_THRESHOLD_PX,
    searchInputSx,
    sidebarSx,
    srOnly,
} from '../styles/chartReader.styles';
import {
    EmptyStateSubtitleTypography,
    EmptyStateTitleTypography,
    HintTypography,
    LabelTypography,
    SectionHeaderTypography,
} from '../styles/chartReader.typography';
import { ChartDataSection } from './ChartDataSection';
import { ChartTypeDropdown, type ChartTypeDropdownHandle } from './ChartTypeDropdown';
import { SearchResultRow } from './SearchResultRow';
import { SecurityDetailsCard } from './SecurityDetailsCard';

// A proper forwarded-ref container so InfiniteOptions can measure scroll position.
// getOptionsBlankContainer() renders <>{children}</> and ignores the ref,
// which means optionsContainerRef inside InfiniteOptions is always null and
// calculateInView() returns early — pagination never fires.
const OptionsContainer = forwardRef<HTMLDivElement, { children: React.ReactNode }>(({ children }, ref) => (
    <div ref={ref}>{children}</div>
));

export function ChartReaderPage() {
    const [inputValue, setInputValue] = useState('');
    const [inputText, setInputText] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedSecurity, setSelectedSecurity] = useState<AccessibilitySecurity | null>(null);
    const [resultsCount, setResultsCount] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedChartType, setSelectedChartType] = useState<ChartTypeCode | ''>('');
    const [isFetchEnabled, setIsFetchEnabled] = useState(false);
    // Bumped on every Load Chart click — used as the key on ChartDataSection so
    // a re-click always remounts the active chart panel and clears any internal
    // selector state, even when React Query serves cached data.
    const [loadVersion, setLoadVersion] = useState(0);
    // Flips once on first user interaction so the verbose welcome aria-label
    // is only announced on initial page load, not every time focus returns to
    // the search input after selecting a security.
    const [hasInteracted, setHasInteracted] = useState(false);
    const resultsContainerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const infiniteOptionsRef = useRef(null);
    const chartTypeDropdownRef = useRef<ChartTypeDropdownHandle>(null);
    const loadChartButtonRef = useRef<HTMLButtonElement>(null);

    // Close dropdown when clicking outside the search container
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Override the browser's Ctrl+F / Cmd+F so it focuses the security search
    // instead of opening the native find-in-page dialog. The page-level search
    // is the primary action on this screen, and an NVDA user would otherwise
    // have to tab back to it.
    useEffect(() => {
        const handleFindShortcut = (event: KeyboardEvent) => {
            const isFindCombo =
                (event.ctrlKey || event.metaKey) &&
                !event.shiftKey &&
                !event.altKey &&
                event.key.toLowerCase() === 'f';
            if (!isFindCombo) return;
            event.preventDefault();
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
        };
        document.addEventListener('keydown', handleFindShortcut);
        return () => document.removeEventListener('keydown', handleFindShortcut);
    }, []);

    const debouncedSetInputValue = useMemo(
        () =>
            debounce((value: string) => {
                setInputValue(value);
                setResultsCount(undefined);
            }, 200),
        [],
    );

    useEffect(() => {
        return () => debouncedSetInputValue.cancel();
    }, [debouncedSetInputValue]);

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setHasInteracted(true);
            setIsLoading(true);
            setInputText(event.target.value);
            setIsDropdownOpen(Boolean(event.target.value));
            debouncedSetInputValue(event.target.value);
        },
        [debouncedSetInputValue],
    );

    const clearSearchInput = useCallback(() => {
        setInputText('');
        setInputValue('');
        setIsDropdownOpen(false);
        debouncedSetInputValue.cancel();
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
    }, [debouncedSetInputValue]);

    const handleClear = useCallback(() => {
        clearSearchInput();
        searchInputRef.current?.focus();
    }, [clearSearchInput]);

    const handleSecuritySelect = useCallback((security: AccessibilitySecurity) => {
        // Show the picked security in the input itself (e.g. "Apple Inc. (AAPL)")
        // so the user can see/hear what's currently selected when focus returns.
        // inputValue stays empty so no fresh search fires; isDropdownOpen=false
        // keeps the result list closed.
        const displayText = `${security.name} (${security.ticker})`;

        // Apply all of this synchronously (flushSync) so that, by the time we move
        // focus below, the results list is already gone AND the chart-type dropdown
        // is rendered + enabled (it enables off selectedSecurity's chart types).
        //
        // Why it matters for NVDA: selecting closes the results list, which unmounts
        // the focused role="option". If focus is moved on a later tick (e.g.
        // setTimeout), the browser first drops focus to <body> in the gap, and NVDA
        // announces the document title ("CFRA Accessibility document") before the
        // dropdown's own label. Committing state first, then focusing in the same
        // task, moves focus straight from the option to the dropdown with no stop
        // on <body> — so NVDA only announces "Select Chart Type".
        flushSync(() => {
            setSelectedSecurity(security);
            // A new security can have a different set of valid chart types, and any
            // already-loaded chart data refers to the old security — reset both so
            // the user starts the chart-type flow fresh.
            setSelectedChartType('');
            setIsFetchEnabled(false);
            setInputText(displayText);
            setInputValue('');
            setIsDropdownOpen(false);
        });
        debouncedSetInputValue.cancel();
        if (searchInputRef.current) {
            searchInputRef.current.value = displayText;
        }

        chartTypeDropdownRef.current?.focus();
    }, [debouncedSetInputValue]);

    const handleChartTypeChange = useCallback((code: ChartTypeCode) => {
        setSelectedChartType(code);
        setIsFetchEnabled(false); // clear previous result when chart type changes
        // Move focus to the Load Chart button after React flushes the state update
        // that enables it (it's disabled until a chart type is selected — and
        // disabled buttons can't accept focus).
        setTimeout(() => loadChartButtonRef.current?.focus(), 50);
    }, []);

    const handleInputFocus = useCallback(() => {
        // Re-open the dropdown only if there's a live search query — not when
        // the input is just displaying the previously-selected security label.
        if (inputValue) {
            setIsDropdownOpen(true);
        }
        // When focus returns and the input is showing the selected-security
        // label, select-all so the next keystroke replaces it cleanly instead
        // of appending to "Apple Inc. (AAPL)".
        if (selectedSecurity && !inputValue) {
            searchInputRef.current?.select();
        }
    }, [inputValue, selectedSecurity]);

    // ArrowDown from the input moves focus to the first result; Escape closes the dropdown
    const handleInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'ArrowDown' && isDropdownOpen) {
                event.preventDefault();
                const first = resultsContainerRef.current?.querySelector<HTMLElement>('[role="option"]');
                first?.focus();
            }
            if (event.key === 'Escape') {
                setIsDropdownOpen(false);
            }
        },
        [isDropdownOpen],
    );

    // ArrowDown/Up navigates between results; Escape returns focus to the input
    const handleResultKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>, source: AccessibilitySecurity) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSecuritySelect(source);
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                (event.currentTarget.nextElementSibling as HTMLElement | null)?.focus();
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                const prev = event.currentTarget.previousElementSibling as HTMLElement | null;
                if (prev?.getAttribute('role') === 'option') {
                    prev.focus();
                } else {
                    searchInputRef.current?.focus();
                }
            } else if (event.key === 'Escape') {
                setIsDropdownOpen(false);
                searchInputRef.current?.focus();
            }
        },
        [handleSecuritySelect],
    );

    const infiniteRequestParams = useMemo(
        () =>
            [
                {
                    q: inputValue,
                    path: SECURITY_SUGGESTER_PATH,
                    size: SECURITY_SUGGESTER_SIZE,
                    config: { enabled: Boolean(inputValue) },
                },
                {
                    queryKeyFirstElement: 'chartReaderSecuritySuggesterQuery',
                    ...getSecuritySuggesterInfiniteRequestParamsConfig(SECURITY_SUGGESTER_SIZE),
                },
            ] as Parameters<SendSingleInfiniteRequest>,
        [inputValue],
    );

    const hasResults = Boolean(inputValue);

    // Compute available chart type options whenever the selected security changes
    const chartTypeOptions = useMemo<ChartTypeOption[]>(
        () =>
            selectedSecurity
                ? getChartTypesForSecurity(selectedSecurity).map((code) => ({
                      code,
                      label: CHART_TYPE_DISPLAY_NAMES[code],
                  }))
                : [],
        [selectedSecurity],
    );

    // Resolve the S3 security type — depends on security sub-type fields and,
    // for Stock, on the selected chart type. Button stays disabled if null.
    // Report date will be added to isLoadChartReady once the backend delivers it.
    const s3SecurityType = useMemo(
        () =>
            selectedSecurity && selectedChartType
                ? resolveS3SecurityType(selectedSecurity, selectedChartType)
                : null,
        [selectedSecurity, selectedChartType],
    );

    // React Query hook — fires only when isFetchEnabled is true and s3SecurityType is resolved.
    // Query key incorporates all three params so a new security/chart type gets a fresh fetch.
    const chartDataQuery = sendSingleRequest<unknown>(
        {
            config: { enabled: isFetchEnabled && Boolean(s3SecurityType) },
            noErrorOnNoKeyValuePairs: true,
        },
        {
            path: `${CHART_DATA_PATH}?security_type=${s3SecurityType ?? ''}&security_id=${selectedSecurity ? resolveSecurityId(selectedSecurity) : ''}&chart_type=${selectedChartType}`,
            requestType: RequestTypes.GET,
            apiName: ApiNames.Wealth,
            queryKeyFirstElement: 'chartReaderChartData',
        },
    );

    const isChartLoading = chartDataQuery.isFetching;
    const isLoadChartReady = Boolean(s3SecurityType) && !isChartLoading;

    // The shared API util (packages/shared/src/utils/api.ts) catches axios errors
    // and resolves the query with the error body tagged `{ isErroredResponse: true,
    // statusCode }`. React Query's own `isError` therefore stays false, so we
    // detect failures off the data instead.
    const erroredResponse = chartDataQuery.data as
        | { isErroredResponse?: boolean; statusCode?: number }
        | undefined;
    const isChartError = erroredResponse?.isErroredResponse === true;
    const errorStatus = erroredResponse?.statusCode;
    const isNotFound = isChartError && errorStatus === 404;

    // Generic "do we have a chart panel to show?" gate — true once the query
    // has returned a non-error payload and isn't currently re-fetching. Payload
    // narrowing happens inside ChartDataSection per chart type, so this page
    // doesn't need to know which response shapes are valid.
    const hasChartData = chartDataQuery.data !== undefined && !isChartLoading && !isChartError;

    const loadChartAriaLabel = isChartLoading
        ? `Loading chart data for ${selectedSecurity?.ticker}, ${selectedChartType ? CHART_TYPE_DISPLAY_NAMES[selectedChartType] : ''}`
        : isLoadChartReady
          ? 'Press Enter to load the chart data'
          : 'Load chart — select a security and chart type to continue';

    const chartErrorMessage = isChartError
        ? isNotFound
            ? 'No Data found'
            : 'Failed to load data. Please try again.'
        : '';

    const handleLoadChart = useCallback(() => {
        setIsFetchEnabled(true);
        setLoadVersion((v) => v + 1);
    }, []);

    return (
        <Box sx={pageContainerSx}>
            {/* ── Left sidebar ── */}
            <Box sx={sidebarSx}>
                <SectionHeaderTypography>Find a Security</SectionHeaderTypography>
                <label htmlFor='security-search-input'>
                    <LabelTypography sx={{ my: 0.5 }}>Security search</LabelTypography>
                </label>

                {/* Screen-reader live region — single source of truth so NVDA
                     doesn't read both "loading" and a stale/sentinel count.
                     InfiniteOptions emits -1 as a "not yet known" marker before
                     the real count arrives, so gate on >= 0 and let "Loading..."
                     win until the request actually settles. */}
                <div role='status' aria-live='polite' aria-atomic='true' style={srOnly}>
                    {!hasResults
                        ? ''
                        : isLoading
                          ? 'Loading results...'
                          : resultsCount !== undefined && resultsCount >= 0
                            ? `${resultsCount} result${resultsCount !== 1 ? 's' : ''} found`
                            : ''}
                </div>

                <Box ref={searchContainerRef} sx={{ position: 'relative' }}>
                    <TextField
                        id='security-search-input'
                        fullWidth
                        size='small'
                        autoFocus
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onKeyDown={handleInputKeyDown}
                        inputRef={searchInputRef}
                        inputProps={{
                            'aria-label': hasInteracted
                                ? 'Search for a security by ticker or name'
                                : 'Welcome to CFRA Accessibility Site. Please type a ticker or name to search for a security',
                            autoComplete: 'off',
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon sx={{ fontSize: '16px', color: '#57626A' }} aria-hidden='true' />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment
                                    position='end'
                                    sx={{
                                        visibility: inputText ? 'visible' : 'hidden',
                                        opacity: inputText ? 1 : 0,
                                        transition: 'opacity 0.1s ease-out, visibility 0.1s ease-out',
                                    }}>
                                    <IconButton
                                        aria-label='Clear search'
                                        onClick={handleClear}
                                        size='small'
                                        sx={{ padding: '2px' }}>
                                        <ClearRoundedIcon sx={{ fontSize: '16px', color: '#57626A' }} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={searchInputSx}
                    />

                    {/* Floating results — absolutely positioned to overlay content below */}
                    {hasResults && isDropdownOpen && (
                        <Box
                            id='security-search-results'
                            ref={resultsContainerRef}
                            role='listbox'
                            aria-label='Security search results'
                            sx={dropdownContainerSx}>
                            <InfiniteOptions<AccessibilitySearchResponse, Hit<AccessibilitySecurity>>
                                ref={infiniteOptionsRef}
                                OptionsContainer={OptionsContainer}
                                infiniteRequestParams={infiniteRequestParams}
                                outerSetOptionsCondition={Boolean(inputValue)}
                                scrollThresholdPx={SCROLL_THRESHOLD_PX}
                                outerContainerRef={resultsContainerRef}
                                scrollContainerRef={resultsContainerRef}
                                setIsLoading={setIsLoading}
                                setNumberOfResults={setResultsCount}
                                getRowsData={(data) => Array.isArray(data) ? data : []}
                                noResultsFoundBoxSxProps={{ p: 1.5 }}
                                loadingContainerStyles={{ p: 1.5 }}
                                RowComponent={({ rowData }) => (
                                    <SearchResultRow
                                        rowData={rowData}
                                        isSelected={selectedSecurity?.sppw_id === rowData._source.sppw_id}
                                        onSelect={handleSecuritySelect}
                                        onKeyDown={handleResultKeyDown}
                                    />
                                )}
                                rowsKeyPrefix={inputValue}
                            />
                        </Box>
                    )}
                </Box>

                <HintTypography sx={{ mt: 0.5, mb: 1 }}>Search by ticker or name</HintTypography>

                {/* ── Chart Type section ── */}
                <Box sx={chartTypeSectionSx}>
                    <SectionHeaderTypography>Chart Configuration</SectionHeaderTypography>
                    <label htmlFor='chart-type-select'>
                        <LabelTypography sx={{ my: 0.5 }}>Chart type</LabelTypography>
                    </label>
                    <ChartTypeDropdown
                        ref={chartTypeDropdownRef}
                        options={chartTypeOptions}
                        value={selectedChartType}
                        onChange={handleChartTypeChange}
                    />
                    <HintTypography sx={{ mt: 0.5 }}>
                        {selectedSecurity ? 'Select a chart type to continue' : 'Select a security first'}
                    </HintTypography>
                </Box>

                {/* ── Load Chart button ── */}
                <Button
                    ref={loadChartButtonRef}
                    fullWidth
                    variant='contained'
                    disabled={!isLoadChartReady}
                    onClick={handleLoadChart}
                    aria-label={loadChartAriaLabel}
                    aria-busy={isChartLoading}
                    disableElevation
                    sx={loadChartButtonSx}>
                    {isChartLoading ? 'Loading...' : 'Load Chart'}
                </Button>

                {/* Error feedback for chart load is shown in the right pane —
                     see the isChartError branch in <main> below. */}
            </Box>

            {/* ── Right content area ── */}
            <Box
                component='main'
                id='main-content'
                tabIndex={-1}
                aria-label='Data display area'
                // Top-anchor (top center) once a security is selected — covers both the
                // Selected Security card and the loaded chart panel. The empty state and
                // error message stay vertically centered.
                sx={selectedSecurity && !isChartError ? mainContentPanelSx : mainContentSx}>
                {!selectedSecurity ? (
                    <Box sx={emptyStateSx} role='status' aria-live='polite'>
                        <SearchIcon sx={{ fontSize: '40px', color: '#3B99A7', opacity: 0.6 }} aria-hidden='true' />
                        <EmptyStateTitleTypography>No security selected</EmptyStateTitleTypography>
                        <EmptyStateSubtitleTypography>
                            Search for a security on the left to load chart data
                        </EmptyStateSubtitleTypography>
                    </Box>
                ) : isChartError ? (
                    // Right-pane error state — role="alert" announces via NVDA
                    // as soon as it appears, no focus needed.
                    <Box sx={emptyStateSx} role='alert' aria-atomic='true'>
                        <EmptyStateTitleTypography sx={{ color: '#d32f2f' }}>
                            {chartErrorMessage}
                        </EmptyStateTitleTypography>
                    </Box>
                ) : hasChartData ? (
                    // Chart-type-agnostic — ChartDataSection routes to the right
                    // panel based on chartType + the raw response shape.
                    <ChartDataSection
                        key={loadVersion}
                        chartType={selectedChartType}
                        chartData={chartDataQuery.data}
                    />
                ) : (
                    <SecurityDetailsCard security={selectedSecurity} />
                )}
            </Box>
        </Box>
    );
}
